import React, { useEffect, useState } from "react";
import Swal from "sweetalert2";
import axiosInstance from "../../../api/axiosInstance";

// ─── Theme (matches Pharmacy.jsx) ────────────────────────────────────────────
const C = { gray:'#5B6B79', blue:'#96C5F7', red:'#9E2B25', orange:'#F29E4C', green:'#2CA87F', purple:'#7c3aed' }
const thStyle = { fontSize:11, fontWeight:700, color:C.gray, textTransform:'uppercase', letterSpacing:'0.05em', padding:'8px 10px', background:'#f8f9fb', borderBottom:'1px solid #e8ecf0', whiteSpace:'nowrap' }
const tdStyle = { fontSize:13, color:'#2d3748', padding:'7px 10px', verticalAlign:'middle', borderBottom:'1px solid #f0f2f5' }
const actionIcon = (color) => ({ width:30, height:30, borderRadius:8, border:'none', cursor:'pointer', background:`${color}22`, color, display:'inline-flex', alignItems:'center', justifyContent:'center', fontSize:15, marginRight:4, flexShrink:0 })
const inputStyle = { width:'100%', height:36, border:'1.5px solid #dde3eb', borderRadius:8, padding:'0 10px', fontSize:13, color:'#1a202c', outline:'none', boxSizing:'border-box' }
const labelStyle = { fontSize:11, fontWeight:600, color:C.gray, display:'block', marginBottom:5, textTransform:'uppercase', letterSpacing:'0.04em' }

const emptyForm = { name:"", contact_person:"", phone:"", email:"", address:"", payment_terms:"", lead_time_days:"", notes:"" }

export default function Vendors() {
  const [vendors, setVendors]   = useState([]);
  const [search, setSearch]     = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [viewItem, setViewItem] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [editId, setEditId]     = useState(null);
  const [form, setForm]         = useState(emptyForm);

  const fetchVendors = async () => {
    try { const res = await axiosInstance.get("getVendors"); setVendors(res.data.data || []); }
    catch { Swal.fire("Error", "Failed to load vendors", "error"); }
  };

  useEffect(() => { fetchVendors(); }, []);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editMode) { await axiosInstance.put(`updateVendor/${editId}`, form); Swal.fire("Success", "Vendor updated", "success"); }
      else           { await axiosInstance.post("addVendor", form);             Swal.fire("Success", "Vendor added",   "success"); }
      resetForm(); fetchVendors();
    } catch { Swal.fire("Error", "Something went wrong!", "error"); }
  };

  const handleEdit = (item) => {
    setForm({ name:item.name||"", contact_person:item.contact_person||"", phone:item.phone||"", email:item.email||"", address:item.address||"", payment_terms:item.payment_terms||"", lead_time_days:item.lead_time_days||"", notes:item.notes||"" });
    setEditId(item.id); setEditMode(true); setModalOpen(true);
  };

  const handleDelete = async (id) => {
    const ok = await Swal.fire({ title:"Are you sure?", text:"Vendor will be deactivated.", icon:"warning", showCancelButton:true, confirmButtonText:"Yes, delete it!" });
    if (ok.isConfirmed) {
      try { await axiosInstance.delete(`deleteVendor/${id}`); Swal.fire("Deleted!", "Vendor deactivated.", "success"); fetchVendors(); }
      catch { Swal.fire("Error", "Something went wrong!", "error"); }
    }
  };

  const resetForm = () => { setForm(emptyForm); setEditMode(false); setEditId(null); setModalOpen(false); };

  const filtered = vendors.filter(v =>
    v.name?.toLowerCase().includes(search.toLowerCase()) ||
    v.contact_person?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <>
      {/* ── Toolbar ── */}
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:8, marginBottom:14 }}>
        <div style={{ position:'relative', maxWidth:340 }}>
          <i className="ti ti-search" style={{ position:'absolute', left:10, top:'50%', transform:'translateY(-50%)', color:C.gray, fontSize:14, pointerEvents:'none' }} />
          <input
            type="text" value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search by name or contact..."
            style={{ ...inputStyle, paddingLeft:32, maxWidth:340 }}
          />
        </div>
        <div style={{ display:'flex', gap:8, alignItems:'center' }}>
          <span style={{ fontSize:11, color:C.gray }}>{filtered.length} record{filtered.length !== 1 ? 's' : ''}</span>
          <button className="bgBtn btn-sm d-flex align-items-center gap-1" style={{ fontSize:13, padding:'5px 12px' }} onClick={() => setModalOpen(true)}>
            <i className="ti ti-plus" style={{ fontSize:14 }} /> Add Vendor
          </button>
        </div>
      </div>

      {/* ── Table ── */}
      <div style={{ overflowX:'auto', borderRadius:8, border:'1px solid #e8ecf0' }}>
        <table style={{ width:'100%', borderCollapse:'collapse' }}>
          <thead>
            <tr>
              <th style={{ ...thStyle, borderLeft:`3px solid ${C.purple}` }}>#</th>
              <th style={thStyle}>Name</th>
              <th style={thStyle}>Contact Person</th>
              <th style={thStyle}>Phone</th>
              <th style={thStyle}>Email</th>
              <th style={thStyle}>Payment Terms</th>
              <th style={thStyle}>Lead Time</th>
              <th style={{ ...thStyle, textAlign:'center' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr>
                <td colSpan={8} style={{ ...tdStyle, textAlign:'center', color:C.gray, padding:'36px 0' }}>
                  <i className="ti ti-database-off" style={{ fontSize:28, display:'block', marginBottom:6, opacity:0.4 }} />
                  No vendors found
                </td>
              </tr>
            ) : filtered.map((item, i) => (
              <tr
                key={item.id}
                style={{ background: i % 2 === 0 ? '#fff' : '#fafbfc' }}
                onMouseEnter={e => e.currentTarget.style.background = `${C.purple}0d`}
                onMouseLeave={e => e.currentTarget.style.background = i % 2 === 0 ? '#fff' : '#fafbfc'}
              >
                <td style={tdStyle}>{i + 1}</td>
                <td style={{ ...tdStyle, fontWeight:500 }}>{item.name}</td>
                <td style={tdStyle}>{item.contact_person || '—'}</td>
                <td style={tdStyle}>{item.phone || '—'}</td>
                <td style={tdStyle}>{item.email || '—'}</td>
                <td style={tdStyle}>
                  {item.payment_terms
                    ? <span style={{ fontSize:11, fontWeight:600, padding:'2px 8px', borderRadius:10, background:`${C.purple}18`, color:C.purple }}>{item.payment_terms}</span>
                    : '—'}
                </td>
                <td style={tdStyle}>{item.lead_time_days != null ? `${item.lead_time_days} days` : '—'}</td>
                <td style={{ ...tdStyle, textAlign:'center', whiteSpace:'nowrap' }}>
                  <button onClick={() => setViewItem(item)} style={actionIcon(C.blue)}  title="View"><i className="ti ti-eye"   /></button>
                  <button onClick={() => handleEdit(item)}  style={actionIcon(C.orange)} title="Edit"><i className="ti ti-edit"  /></button>
                  <button onClick={() => handleDelete(item.id)} style={actionIcon(C.red)} title="Delete"><i className="ti ti-trash" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* ── Add / Edit Modal ── */}
      {modalOpen && (
        <div style={{ position:'fixed', inset:0, zIndex:1050, background:'rgba(0,0,0,0.35)', display:'flex', alignItems:'center', justifyContent:'center' }} onClick={resetForm}>
          <div style={{ background:'#fff', borderRadius:12, width:'90%', maxWidth:720, maxHeight:'90vh', overflowY:'auto', boxShadow:'0 8px 32px rgba(0,0,0,0.18)' }} onClick={e => e.stopPropagation()}>
            <div style={{ padding:'16px 24px', borderBottom:'1px solid #e8ecf0', display:'flex', alignItems:'center', gap:10 }}>
              <div style={{ width:32, height:32, borderRadius:8, background:`${C.purple}18`, display:'flex', alignItems:'center', justifyContent:'center' }}>
                <i className={`ti ${editMode ? 'ti-edit' : 'ti-plus'}`} style={{ color:C.purple, fontSize:16 }} />
              </div>
              <div style={{ fontSize:14, fontWeight:700, color:'#1a202c' }}>{editMode ? 'Edit Vendor' : 'Add Vendor'}</div>
              <button onClick={resetForm} style={{ marginLeft:'auto', width:26, height:26, borderRadius:6, border:'1px solid #e8ecf0', background:'#f8f9fb', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', color:C.gray, fontSize:14 }}>
                <i className="ti ti-x" />
              </button>
            </div>
            <form onSubmit={handleSubmit}>
              <div style={{ padding:'20px 24px' }}>
                <div style={{ display:'flex', flexWrap:'wrap', gap:12 }}>
                  {[
                    { name:'name',           label:'Vendor Name',     required:true  },
                    { name:'contact_person', label:'Contact Person',  required:false },
                    { name:'phone',          label:'Phone',           required:false },
                    { name:'email',          label:'Email',           required:false, type:'email' },
                    { name:'payment_terms',  label:'Payment Terms',   required:false, placeholder:'e.g. Net 30' },
                    { name:'lead_time_days', label:'Lead Time (Days)',required:false, type:'number' },
                  ].map(f => (
                    <div key={f.name} style={{ flex:'1 1 200px' }}>
                      <label style={labelStyle}>{f.label}{f.required && <span style={{ color:C.red }}> *</span>}</label>
                      <input
                        type={f.type || 'text'} name={f.name} value={form[f.name]}
                        onChange={handleChange} required={f.required}
                        placeholder={f.placeholder || ''}
                        style={inputStyle}
                        onFocus={e => e.target.style.borderColor = C.purple}
                        onBlur={e  => e.target.style.borderColor = '#dde3eb'}
                      />
                    </div>
                  ))}
                  {['address','notes'].map(f => (
                    <div key={f} style={{ flex:'1 1 100%' }}>
                      <label style={labelStyle}>{f.charAt(0).toUpperCase() + f.slice(1)}</label>
                      <textarea
                        name={f} value={form[f]} onChange={handleChange} rows={2}
                        style={{ ...inputStyle, height:'auto', padding:'8px 10px', resize:'vertical' }}
                        onFocus={e => e.target.style.borderColor = C.purple}
                        onBlur={e  => e.target.style.borderColor = '#dde3eb'}
                      />
                    </div>
                  ))}
                </div>
              </div>
              <div style={{ padding:'14px 24px', borderTop:'1px solid #e8ecf0', display:'flex', justifyContent:'flex-end', gap:8 }}>
                <button type="button" onClick={resetForm} style={{ padding:'7px 16px', fontSize:13, borderRadius:8, border:'1px solid #dde3eb', background:'#f8f9fb', color:C.gray, cursor:'pointer' }}>Cancel</button>
                <button type="submit" className="bgBtn btn-sm" style={{ padding:'7px 16px', fontSize:13 }}>{editMode ? 'Update Vendor' : 'Add Vendor'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── View Modal ── */}
      {viewItem && (
        <div style={{ position:'fixed', inset:0, zIndex:1050, background:'rgba(0,0,0,0.35)', display:'flex', alignItems:'center', justifyContent:'center' }} onClick={() => setViewItem(null)}>
          <div style={{ background:'#fff', borderRadius:12, width:460, padding:24, boxShadow:'0 8px 32px rgba(0,0,0,0.18)' }} onClick={e => e.stopPropagation()}>
            <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:18 }}>
              <div style={{ width:34, height:34, borderRadius:8, background:`${C.purple}18`, display:'flex', alignItems:'center', justifyContent:'center' }}>
                <i className="ti ti-building-store" style={{ color:C.purple, fontSize:18 }} />
              </div>
              <div style={{ fontSize:14, fontWeight:700, color:'#1a202c' }}>Vendor Details</div>
              <button onClick={() => setViewItem(null)} style={{ marginLeft:'auto', width:26, height:26, borderRadius:6, border:'1px solid #e8ecf0', background:'#f8f9fb', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', color:C.gray, fontSize:14 }}>
                <i className="ti ti-x" />
              </button>
            </div>
            <div style={{ borderTop:'1px solid #f0f2f5', paddingTop:14 }}>
              {[
                ['Vendor Name',     viewItem.name],
                ['Contact Person',  viewItem.contact_person],
                ['Phone',           viewItem.phone],
                ['Email',           viewItem.email],
                ['Payment Terms',   viewItem.payment_terms],
                ['Lead Time',       viewItem.lead_time_days != null ? `${viewItem.lead_time_days} days` : null],
                ['Address',         viewItem.address],
                ['Notes',           viewItem.notes],
              ].map(([label, val]) => (
                <div key={label} style={{ display:'flex', justifyContent:'space-between', padding:'6px 0', borderBottom:'1px solid #f5f7fa', fontSize:13 }}>
                  <span style={{ color:C.gray, fontWeight:600 }}>{label}</span>
                  <span style={{ color:'#2d3748', textAlign:'right', maxWidth:'60%' }}>{val || '—'}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
